import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import BottomNav from '../components/BottomNav';
import PanelToast from '../components/PanelToast';
import KpiStrip from '../components/KpiStrip';
import PanelSection from '../components/PanelSection';
import PanelEmptyState from '../components/PanelEmptyState';
import ConfirmDialog from '../components/ConfirmDialog';

function formatPrice(value) {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function isPlaceholderPromoLabel(label) {
  const normalized = String(label || '').trim().toLowerCase();
  return normalized === 'humor' || normalized === 'test' || normalized === 'placeholder';
}

function getSafeServiceDescription(description) {
  const normalized = String(description || '').trim();

  if (!normalized) {
    return '';
  }

  if (normalized.toLowerCase().includes('masz wielkie usta')) {
    return 'Zabieg modelowania ust z naciskiem na naturalny efekt i komfort klientki.';
  }

  return normalized;
}

const DAYS = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];

const defaultSchedule = [
  { day_of_week: 1, start_time: '09:00', end_time: '17:00', is_active: true },
  { day_of_week: 2, start_time: '09:00', end_time: '17:00', is_active: true },
  { day_of_week: 3, start_time: '09:00', end_time: '17:00', is_active: true },
  { day_of_week: 4, start_time: '09:00', end_time: '17:00', is_active: true },
  { day_of_week: 5, start_time: '09:00', end_time: '17:00', is_active: true },
  { day_of_week: 6, start_time: '09:00', end_time: '14:00', is_active: true },
  { day_of_week: 0, start_time: '09:00', end_time: '14:00', is_active: false },
];

function createEmptyAddon() {
  return { name: '', duration_minutes: 15, price: 0 };
}

function createEmptyServiceForm() {
  return {
    name: '',
    description: '',
    duration_minutes: 60,
    price: '',
    promo_price: '',
    promo_label: '',
    addons: [],
  };
}

function validateServiceForm(service) {
  const nextErrors = { name: '', duration_minutes: '', price: '', promo_price: '' };

  if (!String(service.name || '').trim()) {
    nextErrors.name = 'Podaj nazwę usługi.';
  }

  if (!Number(service.duration_minutes) || Number(service.duration_minutes) < 5) {
    nextErrors.duration_minutes = 'Czas usługi musi mieć co najmniej 5 minut.';
  }

  if (service.price === '' || Number(service.price) <= 0) {
    nextErrors.price = 'Podaj cenę większą od 0.';
  }

  if (service.promo_price !== '' && Number(service.promo_price) >= Number(service.price || 0)) {
    nextErrors.promo_price = 'Cena promocyjna powinna być niższa od regularnej.';
  }

  return nextErrors;
}

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [newService, setNewService] = useState(createEmptyServiceForm());
  const [newServiceErrors, setNewServiceErrors] = useState({ name: '', duration_minutes: '', price: '', promo_price: '' });
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editingService, setEditingService] = useState(createEmptyServiceForm());
  const [editingServiceErrors, setEditingServiceErrors] = useState({ name: '', duration_minutes: '', price: '', promo_price: '' });
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [tab, setTab] = useState('services'); // 'services' | 'schedule'
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const addServiceNameRef = useRef(null);
  const servicesKpis = [
    { key: 'services-total', label: 'Usługi', value: services.length, featured: true },
    { key: 'services-active', label: 'Aktywne', value: services.filter((service) => service.is_active).length, tone: 'soft' },
    { key: 'services-hidden', label: 'Ukryte', value: services.filter((service) => !service.is_active).length, tone: 'neutral' },
    { key: 'services-days', label: 'Dni pracy', value: schedule.filter((day) => day.is_active).length, tone: 'accent' },
  ];

  useEffect(() => {
    api.get('/services/mine').then((r) => setServices(r.data));
    api.get('/availability/schedule').then((r) => {
      if (r.data.length > 0) {
        // Mapuj dane z DB na lokalny stan
        const loaded = defaultSchedule.map((d) => {
          const found = r.data.find((s) => s.day_of_week === d.day_of_week);
          return found ? { ...d, start_time: found.start_time.slice(0, 5), end_time: found.end_time.slice(0, 5), is_active: true } : { ...d, is_active: false };
        });
        setSchedule(loaded);
      }
    });
  }, []);

  async function handleAddService(e) {
    e.preventDefault();
    const nextErrors = validateServiceForm(newService);
    setNewServiceErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setToast({ type: 'error', message: 'Popraw zaznaczone pola nowej usługi.' });
      return;
    }
    try {
      const { data } = await api.post('/services', {
        ...newService,
        promo_price: newService.promo_price || null,
      });
      setServices((prev) => [...prev, data]);
      setNewService(createEmptyServiceForm());
      setNewServiceErrors({ name: '', duration_minutes: '', price: '', promo_price: '' });
      setToast({ type: 'success', message: 'Usługa została dodana.' });
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Błąd dodawania usługi.' });
    }
  }

  async function handleToggleService(id, is_active) {
    await api.put(`/services/${id}`, { is_active: !is_active });
    setServices((prev) => prev.map((s) => s.id === id ? { ...s, is_active: !is_active } : s));
  }

  async function handleDeleteService(serviceId) {
    try {
      await api.delete(`/services/${serviceId}`);
      setServices((prev) => prev.map((service) => (
        service.id === serviceId ? { ...service, is_active: false } : service
      )));
      if (editingServiceId === serviceId) {
        cancelEditing();
      }
      setToast({ type: 'success', message: 'Usługa została usunięta z aktywnej oferty.' });
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Nie udało się usunąć usługi.' });
    }
  }

  function startEditing(service) {
    setEditingServiceId(service.id);
    setEditingServiceErrors({ name: '', duration_minutes: '', price: '', promo_price: '' });
    setEditingService({
      name: service.name,
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      price: service.price,
      promo_price: service.promo_price || '',
      promo_label: service.promo_label || '',
      addons: Array.isArray(service.addons)
        ? service.addons.map((addon) => ({
          id: addon.id,
          name: addon.name,
          duration_minutes: addon.duration_minutes,
          price: addon.price,
          is_active: addon.is_active !== false,
        }))
        : [],
    });
  }

  function cancelEditing() {
    setEditingServiceId(null);
    setEditingService(createEmptyServiceForm());
    setEditingServiceErrors({ name: '', duration_minutes: '', price: '', promo_price: '' });
  }

  async function handleSaveService() {
    if (!editingServiceId) return;
    const nextErrors = validateServiceForm(editingService);
    setEditingServiceErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setToast({ type: 'error', message: 'Popraw zaznaczone pola edytowanej usługi.' });
      return;
    }

    try {
      const payload = {
        name: editingService.name,
        description: editingService.description,
        duration_minutes: Number(editingService.duration_minutes),
        price: Number(editingService.price),
        promo_price: editingService.promo_price === '' ? null : Number(editingService.promo_price),
        promo_label: editingService.promo_label || null,
        addons: editingService.addons,
      };

      const { data } = await api.put(`/services/${editingServiceId}`, payload);
      setServices((prev) => prev.map((s) => (s.id === editingServiceId ? data : s)));
      setToast({ type: 'success', message: 'Usługa została zaktualizowana.' });
      cancelEditing();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Błąd aktualizacji usługi.' });
    }
  }

  async function handleSaveSchedule() {
    setSaving(true);
    try {
      await api.put('/availability/schedule', { schedule });
      setToast({ type: 'success', message: 'Godziny pracy zostały zapisane.' });
    } catch (err) {
      setToast({ type: 'error', message: 'Błąd zapisu grafiku.' });
    } finally {
      setSaving(false);
    }
  }

  function updateScheduleDay(dayOfWeek, field, value) {
    setSchedule((prev) =>
      prev.map((d) => d.day_of_week === dayOfWeek ? { ...d, [field]: value } : d)
    );
  }

  function addAddonToForm(setter) {
    setter((prev) => ({
      ...prev,
      addons: [...prev.addons, createEmptyAddon()],
    }));
  }

  function updateAddon(setter, index, field, value) {
    setter((prev) => ({
      ...prev,
      addons: prev.addons.map((addon, addonIndex) => (
        addonIndex === index ? { ...addon, [field]: value } : addon
      )),
    }));
  }

  function removeAddon(setter, index) {
    setter((prev) => ({
      ...prev,
      addons: prev.addons.filter((_, addonIndex) => addonIndex !== index),
    }));
  }

  function toggleDescription(serviceId) {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  }

  return (
    <div className="panel-page">
      <PanelToast toast={toast} onClose={() => setToast(null)} />

      <div className="panel-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">Oferta i grafik</span>
          <h1 className="panel-title">Ustawienia</h1>
          <p className="panel-subtitle">Buduj ofertę i godziny pracy w bardziej uporządkowanym układzie.</p>
        </div>
      </div>

      <KpiStrip
        items={servicesKpis}
        variant="stats"
        className="services-kpi-strip"
        ariaLabel="Podsumowanie oferty i grafiku"
      />

      <div className="tab-bar">
        <button
          className={`tab-btn ${tab === 'services' ? 'tab-btn--active' : ''}`}
          onClick={() => setTab('services')}
        >
          Usługi
        </button>
        <button
          className={`tab-btn ${tab === 'schedule' ? 'tab-btn--active' : ''}`}
          onClick={() => setTab('schedule')}
        >
          Godziny pracy
        </button>
      </div>
      {/* USŁUGI */}
      {tab === 'services' && (
        <div className="services-page-layout">
          <PanelSection className="section services-page-primary" title={`Lista usług (${services.length})`}>
            {services.length === 0 && (
              <PanelEmptyState
                title="Brak usług w ofercie"
                description="Dodaj pierwszą usługę, żeby klientki mogły ją zobaczyć w rezerwacji online."
                className="panel-empty-state--compact"
                actions={(
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => addServiceNameRef.current?.focus()}
                  >
                    Dodaj pierwszą usługę
                  </button>
                )}
              />
            )}
            {services.map((s) => (
              <div key={s.id} className={`service-row ${!s.is_active ? 'service-row--inactive' : ''}`}>
                {editingServiceId === s.id ? (
                  <div className="service-edit-wrap">
                    <div className="form-group">
                      <label>Nazwa</label>
                      <input
                        type="text"
                        value={editingService.name}
                        onChange={(e) => {
                          setEditingService((prev) => ({ ...prev, name: e.target.value }));
                          setEditingServiceErrors((prev) => ({ ...prev, name: '' }));
                        }}
                        className={editingServiceErrors.name ? 'input-error' : ''}
                        aria-invalid={editingServiceErrors.name ? 'true' : 'false'}
                      />
                      {editingServiceErrors.name && <p className="field-error">{editingServiceErrors.name}</p>}
                    </div>
                    <div className="form-group">
                      <label>Opis</label>
                      <textarea
                        rows="3"
                        value={editingService.description}
                        onChange={(e) => setEditingService((prev) => ({ ...prev, description: e.target.value }))}
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Czas (min)</label>
                        <input
                          type="number"
                          min="5"
                          max="480"
                          value={editingService.duration_minutes}
                          onChange={(e) => {
                            setEditingService((prev) => ({ ...prev, duration_minutes: Number(e.target.value) }));
                            setEditingServiceErrors((prev) => ({ ...prev, duration_minutes: '' }));
                          }}
                          className={editingServiceErrors.duration_minutes ? 'input-error' : ''}
                          aria-invalid={editingServiceErrors.duration_minutes ? 'true' : 'false'}
                        />
                        {editingServiceErrors.duration_minutes && <p className="field-error">{editingServiceErrors.duration_minutes}</p>}
                      </div>
                      <div className="form-group">
                        <label>Cena (zł)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editingService.price}
                          onChange={(e) => {
                            setEditingService((prev) => ({ ...prev, price: Number(e.target.value) }));
                            setEditingServiceErrors((prev) => ({ ...prev, price: '', promo_price: '' }));
                          }}
                          className={editingServiceErrors.price ? 'input-error' : ''}
                          aria-invalid={editingServiceErrors.price ? 'true' : 'false'}
                        />
                        {editingServiceErrors.price && <p className="field-error">{editingServiceErrors.price}</p>}
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Cena promocyjna</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editingService.promo_price}
                          onChange={(e) => {
                            setEditingService((prev) => ({ ...prev, promo_price: e.target.value }));
                            setEditingServiceErrors((prev) => ({ ...prev, promo_price: '' }));
                          }}
                          className={editingServiceErrors.promo_price ? 'input-error' : ''}
                          aria-invalid={editingServiceErrors.promo_price ? 'true' : 'false'}
                        />
                        {editingServiceErrors.promo_price && <p className="field-error">{editingServiceErrors.promo_price}</p>}
                      </div>
                      <div className="form-group">
                        <label>Etykieta promocji</label>
                        <input
                          type="text"
                          value={editingService.promo_label}
                          onChange={(e) => setEditingService((prev) => ({ ...prev, promo_label: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="service-addon-editor">
                      <div className="service-addon-header">
                        <h3 className="section-title">Dodatki do usługi</h3>
                        <button type="button" className="btn-secondary" onClick={() => addAddonToForm(setEditingService)}>Dodaj dodatek</button>
                      </div>
                      {editingService.addons.map((addon, index) => (
                        <div key={addon.id || `edit-addon-${index}`} className="service-addon-row">
                          <div className="form-group">
                            <label>Nazwa dodatku</label>
                            <input value={addon.name} onChange={(e) => updateAddon(setEditingService, index, 'name', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Czas</label>
                            <input type="number" min="0" value={addon.duration_minutes} onChange={(e) => updateAddon(setEditingService, index, 'duration_minutes', Number(e.target.value))} />
                          </div>
                          <div className="form-group">
                            <label>Cena</label>
                            <input type="number" min="0" step="0.01" value={addon.price} onChange={(e) => updateAddon(setEditingService, index, 'price', Number(e.target.value))} />
                          </div>
                          <button type="button" className="btn-ghost" onClick={() => removeAddon(setEditingService, index)}>Usuń</button>
                        </div>
                      ))}
                    </div>
                    <div className="service-edit-actions">
                      <button
                        className="btn-danger"
                        onClick={() => setDeleteTarget({ id: s.id, name: s.name })}
                        type="button"
                      >
                        Usuń usługę
                      </button>
                      <button className="btn-secondary" onClick={cancelEditing} type="button">Anuluj</button>
                      <button className="btn-primary" onClick={handleSaveService} type="button">Zapisz</button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const safeDescription = getSafeServiceDescription(s.description);
                    const isExpanded = Boolean(expandedDescriptions[s.id]);
                    const hasLongDescription = safeDescription.length > 120;
                    const currentPrice = Number(s.effective_price ?? s.promo_price ?? s.price ?? 0);
                    const originalPrice = Number(s.price || 0);
                    const hasPromoPrice = Number(s.promo_price) > 0 && Number(s.promo_price) < originalPrice;

                    return (
                      <>
                    <div className="service-row-info">
                      <span className="service-row-name">{s.name}</span>
                      <span className="service-row-meta">
                        <span className="service-row-duration">{s.duration_minutes} min</span>
                        <span className="service-row-price-current">{formatPrice(currentPrice)} zł</span>
                        {hasPromoPrice && <span className="service-row-price-old">{formatPrice(originalPrice)} zł</span>}
                      </span>
                      {s.promo_label && !isPlaceholderPromoLabel(s.promo_label) && <span className="service-row-promo">{s.promo_label}</span>}
                      {safeDescription && (
                        <>
                          <span className={`service-row-description ${isExpanded ? 'service-row-description--expanded' : 'service-row-description--collapsed'}`}>
                            {safeDescription}
                          </span>
                          {hasLongDescription && (
                            <button
                              type="button"
                              className="service-row-description-toggle"
                              onClick={() => toggleDescription(s.id)}
                            >
                              {isExpanded ? 'Zwiń opis' : 'Rozwiń opis'}
                            </button>
                          )}
                        </>
                      )}
                      {Array.isArray(s.addons) && s.addons.length > 0 && (
                        <div className="service-addon-summary">
                          {s.addons.map((addon) => (
                            <span key={addon.id} className="service-addon-pill">
                              {addon.name} +{addon.price} zł / +{addon.duration_minutes} min
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="service-row-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => startEditing(s)}
                        type="button"
                      >
                        Edytuj
                      </button>
                      <button
                        className={`toggle-btn ${s.is_active ? 'toggle-btn--on' : 'toggle-btn--off'}`}
                        onClick={() => handleToggleService(s.id, s.is_active)}
                        type="button"
                      >
                        {s.is_active ? 'Aktywna' : 'Ukryta'}
                      </button>
                    </div>
                      </>
                    );
                  })()
                )}
              </div>
            ))}
          </PanelSection>

          <PanelSection className="add-service-form services-page-form" title="Dodaj usługę">
            <form onSubmit={handleAddService}>
              <div className="form-group">
                <label>Nazwa</label>
                <input
                  ref={addServiceNameRef}
                  type="text"
                  placeholder="np. Manicure hybrydowy"
                  value={newService.name}
                  onChange={(e) => {
                    setNewService({ ...newService, name: e.target.value });
                    setNewServiceErrors((prev) => ({ ...prev, name: '' }));
                  }}
                  className={newServiceErrors.name ? 'input-error' : ''}
                  aria-invalid={newServiceErrors.name ? 'true' : 'false'}
                />
                {newServiceErrors.name && <p className="field-error">{newServiceErrors.name}</p>}
              </div>
              <div className="form-group">
                <label>Opis</label>
                <textarea
                  rows="3"
                  placeholder="Krótki opis usługi, co obejmuje i dla kogo jest."
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Czas (min)</label>
                  <input
                    type="number"
                    min="5"
                    max="480"
                    value={newService.duration_minutes}
                    onChange={(e) => {
                      setNewService({ ...newService, duration_minutes: parseInt(e.target.value, 10) || 0 });
                      setNewServiceErrors((prev) => ({ ...prev, duration_minutes: '' }));
                    }}
                    className={newServiceErrors.duration_minutes ? 'input-error' : ''}
                    aria-invalid={newServiceErrors.duration_minutes ? 'true' : 'false'}
                  />
                  {newServiceErrors.duration_minutes && <p className="field-error">{newServiceErrors.duration_minutes}</p>}
                </div>
                <div className="form-group">
                  <label>Cena (zł)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="80"
                    value={newService.price}
                    onChange={(e) => {
                      setNewService({ ...newService, price: parseFloat(e.target.value) || '' });
                      setNewServiceErrors((prev) => ({ ...prev, price: '', promo_price: '' }));
                    }}
                    className={newServiceErrors.price ? 'input-error' : ''}
                    aria-invalid={newServiceErrors.price ? 'true' : 'false'}
                  />
                  {newServiceErrors.price && <p className="field-error">{newServiceErrors.price}</p>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Cena promocyjna (opcjonalnie)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="np. 99"
                    value={newService.promo_price}
                    onChange={(e) => {
                      setNewService({ ...newService, promo_price: e.target.value });
                      setNewServiceErrors((prev) => ({ ...prev, promo_price: '' }));
                    }}
                    className={newServiceErrors.promo_price ? 'input-error' : ''}
                    aria-invalid={newServiceErrors.promo_price ? 'true' : 'false'}
                  />
                  {newServiceErrors.promo_price && <p className="field-error">{newServiceErrors.promo_price}</p>}
                </div>
                <div className="form-group">
                  <label>Etykieta promocji</label>
                  <input
                    type="text"
                    placeholder="np. Wiosenna cena"
                    value={newService.promo_label}
                    onChange={(e) => setNewService({ ...newService, promo_label: e.target.value })}
                  />
                </div>
              </div>
              <div className="service-addon-editor">
                <div className="service-addon-header">
                  <h3 className="section-title">Dodatki do usługi</h3>
                  <button type="button" className="btn-secondary" onClick={() => addAddonToForm(setNewService)}>Dodaj dodatek</button>
                </div>
                {newService.addons.map((addon, index) => (
                  <div key={`new-addon-${index}`} className="service-addon-row">
                    <div className="form-group">
                      <label>Nazwa dodatku</label>
                      <input value={addon.name} onChange={(e) => updateAddon(setNewService, index, 'name', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Czas</label>
                      <input type="number" min="0" value={addon.duration_minutes} onChange={(e) => updateAddon(setNewService, index, 'duration_minutes', Number(e.target.value))} />
                    </div>
                    <div className="form-group">
                      <label>Cena</label>
                      <input type="number" min="0" step="0.01" value={addon.price} onChange={(e) => updateAddon(setNewService, index, 'price', Number(e.target.value))} />
                    </div>
                    <button type="button" className="btn-ghost" onClick={() => removeAddon(setNewService, index)}>Usuń</button>
                  </div>
                ))}
              </div>
              <button type="submit" className="btn-primary">Dodaj usługę</button>
            </form>
          </PanelSection>
        </div>
      )}

      {/* GRAFIK */}
      {tab === 'schedule' && (
        <PanelSection
          className="section services-schedule-section"
          title="Godziny pracy"
          actions={(
            <button className="btn-primary" onClick={handleSaveSchedule} disabled={saving}>
              {saving ? 'Zapisuję...' : 'Zapisz godziny'}
            </button>
          )}
        >
          {schedule
            .sort((a, b) => {
              const order = [1, 2, 3, 4, 5, 6, 0];
              return order.indexOf(a.day_of_week) - order.indexOf(b.day_of_week);
            })
            .map((day) => (
              <div key={day.day_of_week} className="schedule-row">
                <div className="schedule-day-toggle">
                  <input
                    type="checkbox"
                    id={`day-${day.day_of_week}`}
                    checked={day.is_active}
                    onChange={(e) => updateScheduleDay(day.day_of_week, 'is_active', e.target.checked)}
                  />
                  <label htmlFor={`day-${day.day_of_week}`} className="schedule-day-name">
                    {DAYS[day.day_of_week]}
                  </label>
                </div>
                {day.is_active && (
                  <div className="schedule-times">
                    <input
                      type="time"
                      value={day.start_time}
                      onChange={(e) => updateScheduleDay(day.day_of_week, 'start_time', e.target.value)}
                    />
                    <span>–</span>
                    <input
                      type="time"
                      value={day.end_time}
                      onChange={(e) => updateScheduleDay(day.day_of_week, 'end_time', e.target.value)}
                    />
                  </div>
                )}
                {!day.is_active && <span className="schedule-closed">Zamknięte</span>}
              </div>
            ))}
        </PanelSection>
      )}

      <BottomNav active="services" />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Usunąć usługę z oferty?"
        description={deleteTarget
          ? `Usługa „${deleteTarget.name}” zostanie ukryta w aktywnej ofercie i przestanie być dostępna w rezerwacji online.`
          : ''}
        confirmLabel="Usuń usługę"
        cancelLabel="Zostaw"
        confirmTone="danger"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          const serviceId = deleteTarget?.id;
          setDeleteTarget(null);
          if (serviceId) {
            void handleDeleteService(serviceId);
          }
        }}
      />
    </div>
  );
}
